// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, withLatestFrom, tap } from 'rxjs/operators';

// 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { User } from 'oidc-client';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  ErrorMessage,
  ReportListItem,
  ReportRequestModel,
  SavedReportTypes,
  ToastModel,
} from '../../../models';
import { AppService } from '../app.service';
import { AppToastService } from '../app-toast.service';
import { OidcService } from '../account/oidc.service';
import { ReportsBaseService } from './reports-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class ReportsService extends ReportsBaseService {
  private _tmjInProgress = false;

  constructor(
    modalService: NgbModal,
    private http: HttpClient,
    private appService: AppService,
    private translate: TranslateService,
    private oidcService: OidcService,
    private appToastService: AppToastService
  ) {
    super(modalService);
  }

  loadReports(): void {
    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.reportsApi;

    this.http
      .get<ReportListItem[]>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((reports: ReportListItem[] | any) => {
        if (reports) {
          const _reports = reports.map((r: ReportListItem) => {
            r.busy = false;
            return r;
          });
          this._reportsList$.next(_reports);
        }
      });
  }

  getReportPdf(
    identifier: string,
    savedReportType = SavedReportTypes.pdg
  ): void {
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.downloadApi +
      `/${identifier}?reportType=${savedReportType}`;

    this._reportIsDownloading$.next(true);
    this._toggleReportLoading(identifier, true);

    this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._reportDownloadError.bind(this)),
        withLatestFrom(this.oidcService.identity$),
        tap(() => {
          this._toggleReportLoading(identifier, false);
        })
      )
      .subscribe(([result, user]) => {
        if (result && user) {
          const fileName = this._createFileName(user, savedReportType);
          this.handleDownloadSuccess(fileName, result);
          this._reportIsDownloading$.next(false);
        }
      });
  }

  regenerateReportPdf(identifier: string): void {
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.regenerateApi +
      `/${identifier}`;

    const report = this._reportsList$.value.find((r) => r.surveyId === identifier);
    if (!report) return;  // report doesn't exist. Don't try and regenerate it.

    const clonedReport = structuredClone(report);
    clonedReport.IsReportInProgress = true;
    const reportList = this._reportsList$.value;
    const index = reportList.findIndex((r) => r.surveyId === identifier);
    reportList.splice(index, 1, clonedReport);
    this._reportsList$.next(reportList);

    this._reportIsDownloading$.next(true);
    this._toggleReportLoading(identifier, true);

    this.http
      .post(url, null)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this)),
        tap(() => {
          this._toggleReportLoading(identifier, false);
        })
      )
      .subscribe((result) => {
        this._reportIsDownloading$.next(false);
        if (this._reportsList$.value) {
          const _reports = this._reportsList$.value;
          _reports.forEach((report) => {
            if (report.surveyId === identifier) {
              report.IsReportInProgress = true;
            }
          });
          this._reportsList$.next(_reports);
        }
      });
  }

  submitReportRequest(model: ReportRequestModel): void {
    if (!model || this._tmjInProgress) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.reportsApi;

    this._reportIsDownloading$.next(true);
    this._tmjInProgress = true;

    this.http
      .post(url, model)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((result) => {
        this._tmjInProgress = false;
        this._reportIsDownloading$.next(false);
        this.appToastService.show({
          header: this.translate.instant('reportFeedbackReportRequested'),
          body: this.translate.instant('reportFeedbackReportRequestedBody'),
          autoHide: true,
          delay: 15000,
          class: 'bg-success text-light',
        } as ToastModel);
      });
  }

  getJournalReportPdf(): void {

    this.appService.setLoading(true);

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.downloadApi + `/getjournalexport/`;


    this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._reportDownloadError.bind(this))
      )
      .subscribe((result) => {
        if (!result) return;

        const fileName = "JournalExport.pdf";
        this.handleDownloadSuccess(fileName, result);
        this.appService.setLoading(false);
      },
        err => this._handleError(err)
      );
  }

  private handleDownloadSuccess(fileName: string, response: Blob) {
    const iOS =
      !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    if (iOS) {
      const reader = new FileReader();
      reader.readAsDataURL(response);
      reader.onloadend = () => {
        const base64data = reader.result;
        this.generateDownloadLink(fileName, base64data as string);
      };
      return;
    }
    this.generateDownloadLink(fileName, window.URL.createObjectURL(response));
  }

  private generateDownloadLink(fileName: string, downloadUrl: string) {
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;

    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(downloadUrl);
  }

  private _reportDownloadError(err: any): Observable<Blob | null> {
    this._handleError(err);
    return of(null);
  }

  private _modelError(err: any): Observable<ReportListItem[] | null> {
    this._handleError(err);
    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);
    this._reportIsLoading$.next(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ReportsService',
      false,
      err
    );

    this.appService.setLastError(error);
  }

  private _createFileName(user: User, savedReportType: SavedReportTypes) {
    if (savedReportType === SavedReportTypes.pdg) {
      const names = [];
      if (user && user.profile && user.profile.given_name) {
        names.push(user.profile.given_name);
      }
      if (user && user.profile && user.profile.family_name) {
        names.push(user.profile.family_name);
      }

      return `EnterpriseEdition_${names.join('_')}_${moment().format(
        'YYYYMMDDHHmm'
      )}.pdf`;
    } else if (savedReportType === SavedReportTypes.tmj) {
      return `TrackMyJourney_${moment().format('YYYYMMDDHHmm')}.pdf`;
    }

    return 'Heartstyles-Report.pdf';
  }
}
